/**
 * FrameworkSoA — Smart Statement of Applicability Engine
 * 
 * Dynamically renders SoA sections based on framework type:
 * - Standards (ISO): Annex A + Management Clauses (with ISO 27002 guidance)
 * - Laws (ZIB, ZZPL): Provisions, Definitions, Requirements, Rights, State Obligations, Procedures, Penalties
 * - Regulations (GDPR, NIS2): Provisions, Requirements, Entity Obligations, Authority Obligations, Penalties
 * - Benchmarks (CIS): Automated Checks, Manual Checks
 * - Custom: All Controls
 * 
 * Features:
 * - ISO 27002 guidance display per control
 * - Pagination
 * - Penalty ↔ Control linking
 * - Evidence & policy attachment
 * - AI guidance per control
 * - Excel export with sheets per section
 * - Applicability toggles where relevant
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import {
  Shield, BookOpen, FileText, ClipboardCheck, Users, Landmark, ListChecks,
  AlertTriangle, Cpu, ClipboardList, List, Building2, Search, Download,
  ChevronDown, ChevronUp, Check, X, Edit, Sparkles, Info, Link as LinkIcon,
  Loader2, Filter, ArrowUpDown, Upload, Eye, EyeOff, AlertCircle, Paperclip,
  ArrowRightLeft, FolderInput, MoreVertical, Globe, Scale, Clock, ToggleLeft, ToggleRight,
  Terminal, RefreshCw, CheckSquare, XSquare, Zap, Settings2, Copy, Play
} from 'lucide-react';
import { Framework, GRCControl, FrameworkSectionConfig, FrameworkLayoutConfig, FrameworkSection } from '../../types/grc';
import { grcService } from '../../services/grcService';
import { aiService } from '../../services/aiService';
import { getFrameworkLayout, categorizeControls, buildPenaltyLinks } from '../../config/frameworkSections';
import { SoAAdvisorCard } from '../dashboard/SoAAdvisorCard';
import { EvidenceLinkingModal } from './EvidenceLinking';
import { ControlEditModal } from './ControlEditModal';
import { SoAControl, Policy } from '../../types/iso27001';
import { Pagination } from '../ui/Pagination';
import { useI18n } from '../../context/I18nContext';
import { authService } from '../../services/authService';

interface FrameworkSoAProps {
  frameworkId: number | null;
  framework?: Framework | null;
  userRole: 'admin' | 'editor' | 'viewer';
  onNavigate?: (tab: string, filters?: any) => void;
}

// Icon resolver
const iconMap: Record<string, React.ComponentType<any>> = {
  Shield, BookOpen, FileText, ClipboardCheck, Users, Landmark, ListChecks,
  AlertTriangle, Cpu, ClipboardList, List, Building2, Globe, Scale, Clock,
};

const getIcon = (name: string) => iconMap[name] || FileText;

// Status colors
const getStatusBadge = (status: string) => {
  switch (status) {
    case 'compliant': return 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300 dark:border-green-800';
    case 'partial': return 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:border-yellow-800';
    case 'non_compliant': return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800';
    case 'not_applicable': return 'bg-gray-50 text-gray-500 border-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:border-gray-600';
    default: return 'bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-700 dark:text-gray-400';
  }
};

const getFulfillmentBadge = (status: string) => {
  switch (status) {
    case 'fulfilled': return 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/30 dark:text-green-300';
    case 'partially_fulfilled': return 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300';
    default: return 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/30 dark:text-red-300';
  }
};

export const FrameworkSoA: React.FC<FrameworkSoAProps> = ({ frameworkId, framework, userRole, onNavigate }) => {
  const { t } = useI18n();
  const [controls, setControls] = useState<GRCControl[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [fulfillmentFilter, setFulfillmentFilter] = useState<string>('all');
  const [applicableFilter, setApplicableFilter] = useState<string>('all');
  const [ownerFilter, setOwnerFilter] = useState<string>('all');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<Partial<GRCControl>>({});
  const [aiActiveId, setAiActiveId] = useState<number | null>(null);
  const [guidanceId, setGuidanceId] = useState<number | null>(null);
  const [policies, setPolicies] = useState<Policy[]>([]);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [evidenceModalControl, setEvidenceModalControl] = useState<GRCControl | null>(null);
  const [editModalControl, setEditModalControl] = useState<GRCControl | null>(null);
  const [moveMenuId, setMoveMenuId] = useState<number | null>(null);
  const [bulkMoveMode, setBulkMoveMode] = useState(false);
  const [selectedControlIds, setSelectedControlIds] = useState<Set<number>>(new Set());
  const [infoExpandedId, setInfoExpandedId] = useState<number | null>(null);
  const [aiExplainId, setAiExplainId] = useState<number | null>(null);
  const [aiExplainResult, setAiExplainResult] = useState<string>('');
  const [aiExplainLoading, setAiExplainLoading] = useState(false);

  // Bulk Actions State
  const [bulkActionsMode, setBulkActionsMode] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [bulkActionProgress, setBulkActionProgress] = useState({ current: 0, total: 0 });
  const [showBulkActionsMenu, setShowBulkActionsMenu] = useState(false);
  
  // Script Generation State
  const [scriptModalControl, setScriptModalControl] = useState<GRCControl | null>(null);
  const [generatedScript, setGeneratedScript] = useState<{ script: string; explanation: string; expectedOutput: string } | null>(null);
  const [scriptLoading, setScriptLoading] = useState(false);
  const [scriptType, setScriptType] = useState<'powershell' | 'bash' | 'python'>('powershell');
  
  // AI Re-process State
  const [reprocessModalControl, setReprocessModalControl] = useState<GRCControl | null>(null);
  const [reprocessResult, setReprocessResult] = useState<{ title: string; description: string; needsUpdate: boolean; changes: string[] } | null>(null);
  const [reprocessLoading, setReprocessLoading] = useState(false);

  // Cross-Framework Mapping State
  const [crossFrameworkModalControl, setCrossFrameworkModalControl] = useState<GRCControl | null>(null);
  const [crossFrameworkMappings, setCrossFrameworkMappings] = useState<Array<{
    id?: number;
    target_control_id: number;
    target_control_code: string;
    target_control_title: string;
    target_framework_id: number;
    target_framework_name: string;
    mapping_type: 'equivalent' | 'overlapping' | 'partial' | 'related';
    relevance_score: number;
    explanation?: string;
    created_by_ai?: boolean;
  }>>([]);
  const [crossFrameworkLoading, setCrossFrameworkLoading] = useState(false);
  const [availableFrameworks, setAvailableFrameworks] = useState<Array<{ id: number; name: string; control_count?: number }>>([]);
  const [selectedTargetFramework, setSelectedTargetFramework] = useState<number | null>(null);

  // Verification State
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationResult, setVerificationResult] = useState<any>(null);
  const [verificationLoading, setVerificationLoading] = useState(false);

  // Dynamic sections from API
  const [dynamicSections, setDynamicSections] = useState<FrameworkSection[]>([]);
  const [sectionsLoaded, setSectionsLoaded] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Track loaded framework to avoid re-fetch
  const loadedFrameworkRef = useRef<number | null>(null);

  // Check permissions (section-based RBAC)
  const canEdit = authService.hasPermission('controls', 'edit') || authService.isGlobalAdmin() || authService.isOrgAdmin();
  const isStandard = framework?.framework_type === 'standard';

  // Detect framework language from control text (Serbian/Croatian/Bosnian vs English)
  const controlLanguage = useMemo(() => {
    if (!controls.length) return undefined;
    const sample = controls.slice(0, 10).map(c => `${c.title} ${c.description || ''}`).join(' ').toLowerCase();
    const hasCyrillic = /[\u0400-\u04FF]/.test(sample);
    const hasSerbian = /\b(zakon|bezbednost|zaštit|obavez|usklađ|primenjiv|informacion|operator|sistem|upravljanj|rizik|mere|politika|procedur|incident|obuka)\b/i.test(sample);
    if (hasCyrillic || hasSerbian) return 'Serbian';
    return undefined; // Let AI service auto-detect
  }, [controls]);
  
  // Determine layout based on framework type
  // For law/regulation frameworks with dynamic sections from DB, build layout from API data
  // For standards/benchmarks, use hardcoded layout enriched with DB section_type toggle
  
  // Load dynamic sections from API for ALL framework types
  useEffect(() => {
    if (!frameworkId) { setDynamicSections([]); setSectionsLoaded(true); return; }
    setSectionsLoaded(false);
    grcService.getFrameworkSections(frameworkId).then(sections => {
      setDynamicSections(sections);
      setSectionsLoaded(true);
    }).catch(() => { setDynamicSections([]); setSectionsLoaded(true); });
  }, [frameworkId]);
  
  // Build layout from dynamic sections or fall back to hardcoded (enriched with DB metadata)
  const layout = useMemo<FrameworkLayoutConfig>(() => {
    const fwType = framework?.framework_type;
    const isLawOrRegulation = fwType === 'law' || fwType === 'regulation';
    
    // For law/regulation frameworks: if dynamic sections exist, build layout entirely from them
    if (isLawOrRegulation && dynamicSections.length > 0) {
      const sections: FrameworkSectionConfig[] = dynamicSections.map(ds => {
        const chapterCodes: string[] = (() => {
          try { return JSON.parse(ds.chapter_codes || '[]'); } catch { return []; }
        })();
        const isInfo = !!ds.is_informational || ds.section_type === 'informational';
        
        return {
          id: ds.section_key,
          label: ds.label,
          icon: ds.icon || 'FileText',
          type: isInfo ? 'provisions' : 'requirements',
          description: ds.description || ds.label,
          showApplicability: !isInfo,
          showEvidence: !isInfo,
          showFulfillment: !isInfo,
          showPenaltyLink: !isInfo,
          showPolicyLink: !isInfo,
          readOnly: isInfo,
          filterFn: (c: GRCControl) => {
            if (c.section_override === ds.section_key) return true;
            const controlChapter = c.chapter_code || '';
            return chapterCodes.includes(controlChapter);
          },
          _dbId: ds.id,
          _isInformational: ds.is_informational,
          _chapterCodes: chapterCodes,
        } as FrameworkSectionConfig & { _dbId?: number; _isInformational?: number; _chapterCodes?: string[] };
      });

      const exportSheetNames: Record<string, string> = {};
      sections.forEach(s => { exportSheetNames[s.id] = s.label; });

      return { frameworkType: fwType || 'custom', sections, exportSheetNames };
    }
    
    // For standard/benchmark/custom frameworks: use hardcoded layout, enriched with DB section IDs
    const hardcoded = framework
      ? getFrameworkLayout(framework.framework_type, framework.code)
      : getFrameworkLayout('custom');
    
    // If we have matching DB sections, enrich each hardcoded section with _dbId and override readOnly
    if (dynamicSections.length > 0) {
      const enriched: FrameworkSectionConfig[] = hardcoded.sections.map(s => {
        const dbSection = dynamicSections.find(ds => ds.section_key === s.id);
        if (dbSection) {
          const isInfo = !!dbSection.is_informational || dbSection.section_type === 'informational';
          return {
            ...s,
            readOnly: isInfo,
            type: isInfo ? 'provisions' : s.type,
            showApplicability: isInfo ? false : s.showApplicability,
            showEvidence: isInfo ? false : s.showEvidence,
            showFulfillment: isInfo ? false : s.showFulfillment,
            _dbId: dbSection.id,
            _isInformational: dbSection.is_informational,
          } as FrameworkSectionConfig & { _dbId?: number; _isInformational?: number };
        }
        return s;
      });
      return { ...hardcoded, sections: enriched };
    }
    
    return hardcoded;
  }, [framework, dynamicSections]);

  // Auto-create DB section records for frameworks that don't have them yet
  // This enables the actionable/informational toggle for ALL frameworks
  useEffect(() => {
    if (!frameworkId || !sectionsLoaded) return;
    // Only create if no sections exist in DB and we have a hardcoded layout
    if (dynamicSections.length === 0 && layout.sections.length > 0 && framework) {
      const sectionDefs = layout.sections.map(s => ({
        key: s.id,
        label: s.label,
        icon: s.icon,
        description: s.description || s.label,
        chapter_codes: [] as string[],
        is_informational: s.readOnly ? 1 : 0,
      }));
      grcService.ensureSections(frameworkId, sectionDefs).then(created => {
        if (created.length > 0) {
          setDynamicSections(created);
        }
      }).catch(() => {});
    }
  }, [frameworkId, sectionsLoaded, dynamicSections.length, layout.sections.length, framework]);

  // Load controls (skip if same framework already loaded)
  useEffect(() => {
    if (!frameworkId) { setLoading(false); return; }
    if (loadedFrameworkRef.current === frameworkId && controls.length > 0) return;
    setLoading(true);
    loadedFrameworkRef.current = frameworkId;
    grcService.getSoA(frameworkId).then(data => {
      setControls(data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [frameworkId]);

  // Load policies for this framework from API
  useEffect(() => {
    if (!frameworkId) { setPolicies([]); return; }
    grcService.getDocuments(frameworkId).then(docs => {
      // Map GRCDocument to Policy shape used by the inline edit form
      const mapped = docs.map(d => ({
        id: String(d.id),
        title: d.title,
        sections: [],
        version: d.version || '1.0',
        createdDate: '',
        lastUpdated: '',
        nextReviewDate: d.next_review_date || '',
        status: (d.status === 'approved' ? 'approved' : d.status === 'draft' ? 'draft' : 'review') as 'draft' | 'review' | 'approved',
        generatedByAI: false,
        owner: d.owner || '',
      }));
      setPolicies(mapped);
    }).catch(() => setPolicies([]));
  }, [frameworkId]);

  // Set default active section only on initial framework load, NOT on toggle changes
  const prevFrameworkIdRef = useRef<number | null>(null);
  useEffect(() => {
    if (layout.sections.length > 0) {
      // Only reset to first section when the framework itself changes, not on section toggle
      if (prevFrameworkIdRef.current !== frameworkId) {
        setActiveSection(layout.sections[0].id);
        setCurrentPage(1);
        prevFrameworkIdRef.current = frameworkId;
      } else {
        // Section toggle happened — check if the current section still exists; if yes keep it
        const currentExists = layout.sections.some(s => s.id === activeSection);
        if (!currentExists) {
          setActiveSection(layout.sections[0].id);
          setCurrentPage(1);
        }
      }
    }
  }, [layout]);

  // Categorize controls into sections
  const categorized = useMemo(() => {
    try {
      return categorizeControls(controls || [], layout);
    } catch (e) {
      console.error('Error categorizing controls:', e);
      // Fallback: put all controls in first section
      const result: Record<string, GRCControl[]> = {};
      for (const section of layout.sections) {
        result[section.id] = [];
      }
      if (layout.sections.length > 0) {
        result[layout.sections[0].id] = controls || [];
      }
      return result;
    }
  }, [controls, layout]);

  // Build penalty links
  const penaltyLinks = useMemo(() => {
    try {
      const penaltySections = layout.sections.filter(s => s.type === 'penalties');
      const requirementSections = layout.sections.filter(s => !s.readOnly);
      const penalties = penaltySections.flatMap(s => categorized[s.id] || []);
      const requirements = requirementSections.flatMap(s => categorized[s.id] || []);
      return buildPenaltyLinks(penalties, requirements);
    } catch (e) {
      console.error('Error building penalty links:', e);
      return new Map<number, number[]>();
    }
  }, [categorized, layout]);

  // Section counts
  const sectionCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const section of layout.sections) {
      counts[section.id] = (categorized[section.id] || []).length;
    }
    return counts;
  }, [categorized, layout]);

  // Current section config
  const currentSection = layout.sections.find(s => s.id === activeSection);
  const currentControls = categorized[activeSection] || [];

  // Extract unique owners for filter dropdown
  const uniqueOwners = useMemo(() => {
    const owners = new Set<string>();
    controls.forEach(c => {
      if (c.owner) owners.add(c.owner);
    });
    return Array.from(owners).sort();
  }, [controls]);

  // Filter  
  const filteredControls = useMemo(() => {
    let result = currentControls;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      result = result.filter(c =>
        c.title.toLowerCase().includes(q) ||
        (c.description || '').toLowerCase().includes(q) ||
        (c.control_code || '').toLowerCase().includes(q) ||
        (c.article_code || '').toLowerCase().includes(q) ||
        (c.chapter_title || '').toLowerCase().includes(q)
      );
    }
    if (statusFilter !== 'all') {
      result = result.filter(c => c.status === statusFilter);
    }
    if (fulfillmentFilter !== 'all') {
      result = result.filter(c => c.fulfillment_status === fulfillmentFilter);
    }
    if (applicableFilter !== 'all') {
      if (applicableFilter === 'applicable') {
        result = result.filter(c => c.applicable === 1);
      } else if (applicableFilter === 'not_applicable') {
        result = result.filter(c => c.applicable === 0);
      } else if (applicableFilter === 'partial') {
        result = result.filter(c => c.applicable === 2);
      }
    }
    if (ownerFilter !== 'all') {
      if (ownerFilter === 'unassigned') {
        result = result.filter(c => !c.owner);
      } else {
        result = result.filter(c => c.owner === ownerFilter);
      }
    }
    return result;
  }, [currentControls, searchQuery, statusFilter, fulfillmentFilter, applicableFilter, ownerFilter]);

  // Sort
  const sortedControls = useMemo(() => {
    const sorted = [...filteredControls].sort((a, b) => {
      const codeA = a.control_code || a.article_code || `${a.id}`;
      const codeB = b.control_code || b.article_code || `${b.id}`;
      return codeA.localeCompare(codeB, undefined, { numeric: true, sensitivity: 'base' });
    });
    return sortOrder === 'asc' ? sorted : sorted.reverse();
  }, [filteredControls, sortOrder]);

  // Paginate
  const paginatedControls = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedControls.slice(start, start + pageSize);
  }, [sortedControls, currentPage, pageSize]);
  const totalPages = Math.ceil(sortedControls.length / pageSize);

  // Edit handlers
  const startEdit = (control: GRCControl) => {
    if (!canEdit || currentSection?.readOnly) return;
    // Open the edit modal instead of inline editing
    setEditModalControl(control);
  };

  const handleEditModalSave = (updatedControl: GRCControl) => {
    setControls(prev => prev.map(c => c.id === updatedControl.id ? updatedControl : c));
    setEditModalControl(null);
  };

  const saveEdit = async () => {
    if (!editingId) return;
    try {
      await grcService.updateControl({ id: editingId, ...editForm });
      setControls(prev => prev.map(c => c.id === editingId ? { ...c, ...editForm } : c));
      setEditingId(null);
    } catch (e) {
      console.error('Failed to save:', e);
    }
  };

  // Move control to a different section
  const moveControlToSection = async (controlId: number, targetSectionId: string) => {
    try {
      await grcService.updateControl({ id: controlId, section_override: targetSectionId } as any);
      setControls(prev => prev.map(c => c.id === controlId ? { ...c, section_override: targetSectionId } : c));
      setMoveMenuId(null);
    } catch (e) {
      console.error('Failed to move control:', e);
    }
  };

  // Reset section override (return to AI-assigned section)
  const resetControlSection = async (controlId: number) => {
    try {
      await grcService.updateControl({ id: controlId, section_override: null } as any);
      setControls(prev => prev.map(c => c.id === controlId ? { ...c, section_override: null } : c));
      setMoveMenuId(null);
    } catch (e) {
      console.error('Failed to reset control section:', e);
    }
  };

  // Bulk move selected controls
  const bulkMoveControls = async (targetSectionId: string) => {
    try {
      const promises = Array.from(selectedControlIds).map(id =>
        grcService.updateControl({ id, section_override: targetSectionId } as any)
      );
      await Promise.all(promises);
      setControls(prev => prev.map(c => 
        selectedControlIds.has(c.id) ? { ...c, section_override: targetSectionId } : c
      ));
      setSelectedControlIds(new Set());
      setBulkMoveMode(false);
    } catch (e) {
      console.error('Failed to bulk move:', e);
    }
  };

  const toggleControlSelection = (id: number) => {
    setSelectedControlIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Select all controls in current view
  const selectAllControls = () => {
    const allIds = new Set(filteredControls.map(c => c.id));
    setSelectedControlIds(allIds);
  };

  // Clear selection
  const clearSelection = () => {
    setSelectedControlIds(new Set());
  };

  // Bulk set applicable/not applicable
  const bulkSetApplicable = async (applicable: 0 | 1) => {
    setBulkActionLoading(true);
    setBulkActionProgress({ current: 0, total: selectedControlIds.size });
    try {
      const ids = Array.from(selectedControlIds);
      for (let i = 0; i < ids.length; i++) {
        await grcService.updateControl({ id: ids[i], applicable } as any);
        setBulkActionProgress({ current: i + 1, total: ids.length });
      }
      setControls(prev => prev.map(c =>
        selectedControlIds.has(c.id) ? { ...c, applicable } : c
      ));
      setSelectedControlIds(new Set());
      setBulkActionsMode(false);
    } catch (e) {
      console.error('Failed to bulk set applicable:', e);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk generate verification scripts
  const bulkGenerateScripts = async () => {
    if (!framework) return;
    setBulkActionLoading(true);
    const selectedControls = controls.filter(c => selectedControlIds.has(c.id));
    setBulkActionProgress({ current: 0, total: selectedControls.length });
    
    try {
      for (let i = 0; i < selectedControls.length; i++) {
        const control = selectedControls[i];
        setBulkActionProgress({ current: i + 1, total: selectedControls.length });
        
        const result = await aiService.generateVerificationScript(
          { code: control.control_code || '', title: control.title, description: control.description || '', check_type: control.check_type },
          framework.name,
          scriptType
        );
        
        // Save script to implementation_notes
        await grcService.updateControl({
          id: control.id,
          implementation_notes: `## Verification Script (${scriptType})\n\n\`\`\`${scriptType}\n${result.script}\n\`\`\`\n\n### Explanation\n${result.explanation}\n\n### Expected Output\n${result.expectedOutput}`,
        } as any);
      }
      
      // Reload controls
      if (frameworkId) {
        const updatedControls = await grcService.getSoA(frameworkId);
        setControls(updatedControls);
      }
      
      setSelectedControlIds(new Set());
      alert(`Generated ${selectedControls.length} verification scripts!`);
    } catch (e) {
      console.error('Failed to generate scripts:', e);
      alert('Failed to generate scripts. Please try again.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Bulk AI re-process controls (fix title/description)
  const bulkReprocessControls = async () => {
    if (!framework) return;
    setBulkActionLoading(true);
    const selectedControls = controls.filter(c => selectedControlIds.has(c.id));
    setBulkActionProgress({ current: 0, total: selectedControls.length });
    
    try {
      const results = await aiService.batchReprocessControls(
        selectedControls.map(c => ({
          id: c.id,
          code: c.control_code || c.article_code || '',
          title: c.title,
          description: c.description || '',
          guidance: c.guidance || undefined
        })),
        framework.name,
        (current, total) => setBulkActionProgress({ current, total })
      );
      
      // Update controls that need changes
      let updatedCount = 0;
      for (const result of results) {
        if (result.needsUpdate) {
          await grcService.updateControl({
            id: result.id,
            title: result.title,
            description: result.description,
          } as any);
          updatedCount++;
        }
      }
      
      // Reload controls
      if (frameworkId) {
        const updatedControls = await grcService.getSoA(frameworkId);
        setControls(updatedControls);
      }
      
      setSelectedControlIds(new Set());
      alert(`Re-processed ${selectedControls.length} controls. Updated ${updatedCount} with corrections.`);
    } catch (e) {
      console.error('Failed to re-process controls:', e);
      alert('Failed to re-process controls. Please try again.');
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Single control script generation
  const handleGenerateScript = async (control: GRCControl) => {
    if (!framework) return;
    setScriptModalControl(control);
    setGeneratedScript(null);
    setScriptLoading(true);
    
    try {
      const result = await aiService.generateVerificationScript(
        { code: control.control_code || '', title: control.title, description: control.description || '', check_type: control.check_type },
        framework.name,
        scriptType
      );
      setGeneratedScript(result);
    } catch (e) {
      console.error('Failed to generate script:', e);
      alert('Failed to generate script. Please try again.');
      setScriptModalControl(null);
    } finally {
      setScriptLoading(false);
    }
  };

  // Save generated script to control
  const handleSaveScript = async () => {
    if (!scriptModalControl || !generatedScript) return;
    try {
      await grcService.updateControl({
        id: scriptModalControl.id,
        implementation_notes: `## Verification Script (${scriptType})\n\n\`\`\`${scriptType}\n${generatedScript.script}\n\`\`\`\n\n### Explanation\n${generatedScript.explanation}\n\n### Expected Output\n${generatedScript.expectedOutput}`,
      } as any);
      
      setControls(prev => prev.map(c =>
        c.id === scriptModalControl.id 
          ? { ...c, implementation_notes: generatedScript.script } 
          : c
      ));
      
      setScriptModalControl(null);
      setGeneratedScript(null);
    } catch (e) {
      console.error('Failed to save script:', e);
    }
  };

  // Single control AI re-process (full content extraction)
  const handleReprocessControl = async (control: GRCControl) => {
    if (!framework) return;
    setReprocessModalControl(control);
    setReprocessResult(null);
    setReprocessLoading(true);
    
    try {
      // Use the new backend API that extracts ALL content fields
      const result = await grcService.reprocessControl(control.id);
      
      if (result.success) {
        // Reload the control to get updated data
        const updatedControls = await grcService.getSoA(framework.id);
        setControls(updatedControls);
        
        alert(`Control ${control.control_code} reprocessed successfully. ${result.fields_updated || 0} fields updated.`);
        setReprocessModalControl(null);
      } else {
        // Fallback to old AI reprocess if backend fails
        const fallbackResult = await aiService.reprocessControlContent(
          { code: control.control_code || control.article_code || '', title: control.title, description: control.description || '', guidance: control.guidance || undefined },
          framework.name
        );
        setReprocessResult(fallbackResult);
      }
    } catch (e) {
      console.error('Failed to re-process control:', e);
      alert('Failed to re-process control. Please try again.');
      setReprocessModalControl(null);
    } finally {
      setReprocessLoading(false);
    }
  };

  // Apply re-processed changes
  const handleApplyReprocess = async () => {
    if (!reprocessModalControl || !reprocessResult) return;
    try {
      await grcService.updateControl({
        id: reprocessModalControl.id,
        title: reprocessResult.title,
        description: reprocessResult.description,
      } as any);
      
      setControls(prev => prev.map(c =>
        c.id === reprocessModalControl.id 
          ? { ...c, title: reprocessResult.title, description: reprocessResult.description } 
          : c
      ));
      
      setReprocessModalControl(null);
      setReprocessResult(null);
    } catch (e) {
      console.error('Failed to apply changes:', e);
    }
  };

  // Open cross-framework mapping modal
  const handleOpenCrossFrameworkMapping = async (control: GRCControl) => {
    setCrossFrameworkModalControl(control);
    setCrossFrameworkLoading(true);
    setCrossFrameworkMappings([]);
    
    try {
      // Load existing mappings for this control
      const existingMappings = await grcService.getControlMappings(control.id);
      
      // Transform to our state format
      const mappingsForState = existingMappings.map((m: any) => ({
        id: m.id,
        target_control_id: m.target_control_id,
        target_control_code: m.target_control_code || '',
        target_control_title: m.target_control_title || '',
        target_framework_id: m.target_framework_id,
        target_framework_name: m.target_framework_name || '',
        mapping_type: m.mapping_type || 'related',
        relevance_score: m.relevance_score || 50,
        explanation: m.explanation || '',
        created_by_ai: m.created_by_ai === 1 || m.created_by_ai === true,
      }));
      
      setCrossFrameworkMappings(mappingsForState);
      
      // Load available frameworks (exclude current framework)
      const frameworks = await grcService.getFrameworks();
      setAvailableFrameworks(frameworks.filter((f: any) => f.id !== frameworkId));
    } catch (e) {
      console.error('Failed to load cross-framework mappings:', e);
    } finally {
      setCrossFrameworkLoading(false);
    }
  };

  // Run AI analysis to find related controls in target framework
  const handleAiCrossFrameworkMapping = async () => {
    if (!crossFrameworkModalControl || !selectedTargetFramework || !framework) return;
    setCrossFrameworkLoading(true);
    
    try {
      // Get controls from target framework
      const targetControls = await grcService.getControlsForMapping(selectedTargetFramework);
      const targetFramework = availableFrameworks.find(f => f.id === selectedTargetFramework);
      
      if (!targetControls.length || !targetFramework) {
        alert('No controls found in target framework');
        return;
      }
      
      // Call AI service to find mappings
      const aiMappings = await aiService.mapControlsAcrossFrameworks(
        { code: crossFrameworkModalControl.control_code || '', title: crossFrameworkModalControl.title, description: crossFrameworkModalControl.description || '' },
        framework.name,
        targetFramework.name,
        targetControls.map((c: any) => ({ id: c.id, code: c.control_code || '', title: c.title, description: c.description || '' }))
      );
      
      // Add to mappings (merge with existing, don't duplicate)
      const existingTargetIds = new Set(crossFrameworkMappings.map(m => m.target_control_id));
      const newMappings = aiMappings
        .filter((m: any) => !existingTargetIds.has(m.target_control_id))
        .map((m: any) => ({
          target_control_id: m.target_control_id,
          target_control_code: m.target_control_code,
          target_control_title: m.target_control_title,
          target_framework_id: selectedTargetFramework,
          target_framework_name: targetFramework.name,
          mapping_type: m.mapping_type,
          relevance_score: m.relevance_score,
          explanation: m.explanation,
          created_by_ai: true,
        }));
      
      setCrossFrameworkMappings(prev => [...prev, ...newMappings]);
    } catch (e) {
      console.error('Failed to run AI cross-framework mapping:', e);
      alert('AI mapping failed. Please try again.');
    } finally {
      setCrossFrameworkLoading(false);
    }
  };

  // Save cross-framework mappings
  const handleSaveCrossFrameworkMappings = async () => {
    if (!crossFrameworkModalControl) return;
    setCrossFrameworkLoading(true);
    
    try {
      const mappingsToSave = crossFrameworkMappings.map(m => ({
        source_control_id: crossFrameworkModalControl.id,
        target_control_id: m.target_control_id,
        mapping_type: m.mapping_type,
        relevance_score: m.relevance_score,
        explanation: m.explanation,
        created_by_ai: m.created_by_ai ? 1 : 0,
      }));
      
      await grcService.saveControlMappings(mappingsToSave);
      
      // Update control's mapping count in local state
      setControls(prev => prev.map(c =>
        c.id === crossFrameworkModalControl.id
          ? { ...c, cross_framework_count: crossFrameworkMappings.length }
          : c
      ));
      
      setCrossFrameworkModalControl(null);
    } catch (e) {
      console.error('Failed to save mappings:', e);
      alert('Failed to save mappings. Please try again.');
    } finally {
      setCrossFrameworkLoading(false);
    }
  };

  // Delete a mapping
  const handleDeleteCrossFrameworkMapping = (targetControlId: number) => {
    setCrossFrameworkMappings(prev => prev.filter(m => m.target_control_id !== targetControlId));
  };

  // Verify extraction quality
  const handleVerifyExtraction = async () => {
    if (!frameworkId) return;
    setShowVerificationModal(true);
    setVerificationLoading(true);
    
    try {
      const result = await grcService.verifyExtraction(frameworkId);
      setVerificationResult(result);
    } catch (e) {
      console.error('Verification failed:', e);
      setVerificationResult({ success: false, error: 'Verification failed' });
    } finally {
      setVerificationLoading(false);
    }
  };

  // Re-process sections from control codes
  const handleReprocessSections = async () => {
    if (!frameworkId) return;
    setReprocessLoading(true);
    
    try {
      const result = await grcService.reprocessSections(frameworkId, true);
      if (result.success) {
        alert(`Sections re-processed: ${result.sections_created} sections created from ${result.chapters_created} chapters.`);
        // Reload sections and controls
        const sections = await grcService.getFrameworkSections(frameworkId);
        setDynamicSections(sections);
        const newControls = await grcService.getSoA(frameworkId);
        setControls(newControls);
        // Re-run verification
        const newVerification = await grcService.verifyExtraction(frameworkId);
        setVerificationResult(newVerification);
      } else {
        alert('Re-process failed: ' + (result.error || 'Unknown error'));
      }
    } catch (e) {
      console.error('Re-process failed:', e);
      alert('Re-process failed. Check console for details.');
    } finally {
      setReprocessLoading(false);
    }
  };

  // Toggle section informational status (works for all framework types)
  const toggleSectionInformational = useCallback(async (sectionConfig: FrameworkSectionConfig & { _dbId?: number }) => {
    if (!sectionConfig._dbId || !frameworkId) return;
    try {
      // For standard frameworks without chapter_codes, send control IDs for bulk update
      const sectionControls = categorized[sectionConfig.id] || [];
      const controlIds = sectionControls.map(c => c.id);
      
      const result = await grcService.toggleSectionInformational(sectionConfig._dbId, frameworkId, controlIds);
      if (result.success) {
        // Reload dynamic sections to reflect change
        const updated = await grcService.getFrameworkSections(frameworkId);
        setDynamicSections(updated);
        // Reload controls to reflect applicable changes
        const newControls = await grcService.getSoA(frameworkId);
        setControls(newControls);
      }
    } catch (e) {
      console.error('Failed to toggle section informational:', e);
    }
  }, [frameworkId, categorized]);

  // Map GRCControl to SoAControl for AI advisor
  const toSoAControl = (c: GRCControl): SoAControl => ({
    id: c.control_code || `ctrl-${c.id}`,
    name: c.title,
    description: c.description || '',
    applicable: c.applicable === 1,
    justification: c.applicability_justification || '',
    implementationStatus: c.status === 'compliant' ? 'implemented' : c.status === 'partial' ? 'in_progress' : 'not_started',
    owner: c.owner || '',
    fulfillmentRationale: c.fulfillment_rationale || '',
    iso27002Guidance: c.guidance || c.implementation_notes || '',
    coveredByPolicies: c.suggested_policy_names || [],
    implementationSteps: c.implementation_steps || [],
  });

  const handleApplyAI = async (controlId: number, justification: string, owner: string, steps: string[], _docs: string[], summary: string) => {
    const updates: Partial<GRCControl> = {
      applicability_justification: justification,
      owner,
      implementation_steps: steps,
      implementation_notes: summary,
    };
    await grcService.updateControl({ id: controlId, ...updates });
    setControls(prev => prev.map(c => c.id === controlId ? { ...c, ...updates } : c));
    setAiActiveId(null);
  };

  // Excel export
  const exportToExcel = () => {
    // Build CSV with section headers (multi-sheet via separate exports)
    const allRows: string[][] = [['Section', 'Code', 'Title', 'Description', 'Status', 'Fulfillment', 'Owner', 'Applicable', 'Justification', 'Notes']];
    
    for (const section of layout.sections) {
      const sectionControls = categorized[section.id] || [];
      for (const c of sectionControls) {
        allRows.push([
          section.label,
          c.control_code || '',
          c.title,
          c.description || '',
          c.status,
          c.fulfillment_status || '',
          c.owner || '',
          c.applicable === 1 ? 'Yes' : c.applicable === 2 ? 'Partial' : 'No',
          c.applicability_justification || '',
          c.implementation_notes || '',
        ]);
      }
    }

    const csvContent = allRows.map(row => row.map(cell => `"${(cell || '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `SoA_${framework?.code || 'export'}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        <span className="ml-3 text-gray-500">Loading framework controls...</span>
      </div>
    );
  }

  if (!frameworkId) {
    return (
      <div className="text-center py-12 text-gray-500 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
        <Shield className="w-12 h-12 mx-auto mb-3 opacity-50" />
        <p>Select a framework to view Statement of Applicability</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Section Tabs */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-2">
        <div className="flex flex-wrap gap-1">
          {layout.sections.map(section => {
            const Icon = getIcon(section.icon);
            const count = sectionCounts[section.id] || 0;
            const isActive = activeSection === section.id;
            
            return (
              <button
                key={section.id}
                onClick={() => { setActiveSection(section.id); setSearchQuery(''); setStatusFilter('all'); setFulfillmentFilter('all'); setApplicableFilter('all'); setOwnerFilter('all'); setCurrentPage(1); }}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 shadow-sm'
                    : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                }`}
                title={section.description}
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{section.label}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full ${isActive ? 'bg-indigo-200 dark:bg-indigo-800 text-indigo-800 dark:text-indigo-200' : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300'}`}>
                  {count}
                </span>
                {section.readOnly && (
                  <Eye className="w-3 h-3 text-gray-400" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Section Info Banner */}
      {currentSection && (
        <div className={`rounded-lg p-3 border text-sm flex items-center gap-3 ${
          currentSection.readOnly
            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300'
            : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400'
        }`}>
          <Info className="w-4 h-4 flex-shrink-0" />
          <span>{currentSection.description}</span>
          {currentSection.readOnly && (
            <span className="text-xs font-medium px-2 py-0.5 bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 rounded">
              Informational — No compliance action needed
            </span>
          )}
          {currentSection.readOnly && (
            <span className="text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1">
              <Info className="w-3 h-3" /> Click ℹ on any control for AI-powered explanation
            </span>
          )}
          {/* Toggle informational for dynamic sections */}
          {canEdit && (currentSection as any)._dbId && (
            <button
              onClick={() => toggleSectionInformational(currentSection as any)}
              className={`ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                currentSection.readOnly
                  ? 'bg-blue-100 dark:bg-blue-800/50 border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-200 hover:bg-blue-200 dark:hover:bg-blue-800'
                  : 'bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
              title={currentSection.readOnly ? 'Mark this section as actionable (controls will become applicable)' : 'Mark this section as informational (controls will become non-applicable)'}
            >
              {currentSection.readOnly ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
              {currentSection.readOnly ? 'Mark Actionable' : 'Mark Informational'}
            </button>
          )}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={`Search ${currentSection?.label || 'controls'}...`}
            value={searchQuery}
            onChange={e => { setSearchQuery(e.target.value); setCurrentPage(1); }}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
          />
        </div>
        {currentSection && !currentSection.readOnly && (
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white">
            <option value="all">All Statuses</option>
            <option value="compliant">Compliant</option>
            <option value="partial">Partial</option>
            <option value="non_compliant">Non-Compliant</option>
            <option value="not_assessed">Not Assessed</option>
            <option value="not_applicable">N/A</option>
          </select>
        )}
        {currentSection && !currentSection.readOnly && (
          <select value={fulfillmentFilter} onChange={e => { setFulfillmentFilter(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white">
            <option value="all">Fulfillment</option>
            <option value="fulfilled">Fulfilled</option>
            <option value="partially_fulfilled">Partially Fulfilled</option>
            <option value="not_fulfilled">Not Fulfilled</option>
          </select>
        )}
        {currentSection && !currentSection.readOnly && (
          <select value={applicableFilter} onChange={e => { setApplicableFilter(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white">
            <option value="all">Applicability</option>
            <option value="applicable">Applicable</option>
            <option value="not_applicable">Not Applicable</option>
            <option value="partial">Partially Applicable</option>
          </select>
        )}
        {currentSection && !currentSection.readOnly && (
          <select value={ownerFilter} onChange={e => { setOwnerFilter(e.target.value); setCurrentPage(1); }}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white">
            <option value="all">Owner</option>
            <option value="unassigned">Unassigned</option>
            {uniqueOwners.map(owner => (
              <option key={owner} value={owner}>{owner}</option>
            ))}
          </select>
        )}
        <button onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
          className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 text-sm">
          <ArrowUpDown className="w-4 h-4" /> {sortOrder === 'asc' ? 'A→Z' : 'Z→A'}
        </button>
        <button onClick={exportToExcel}
          className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 text-sm">
          <Download className="w-4 h-4" /> Export Excel
        </button>
        {canEdit && (
          <button 
            onClick={handleVerifyExtraction}
            className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 text-amber-700 dark:text-amber-300 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/40 text-sm"
            title="Verify extraction quality and repair sections"
          >
            <AlertCircle className="w-4 h-4" /> Verify & Repair
          </button>
        )}
        {framework?.pdf_file && (
          <a 
            href={`/api/regulation-pdf.php?framework=${framework.code}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 text-sm"
            title={`Download ${framework.name} PDF`}
          >
            <FileText className="w-4 h-4" /> Download Regulation
          </a>
        )}
        {canEdit && (
          <button onClick={() => { setBulkActionsMode(!bulkActionsMode); setBulkMoveMode(false); setSelectedControlIds(new Set()); setShowBulkActionsMenu(false); }}
            className={`flex items-center gap-2 px-3 py-2 border rounded-lg text-sm transition-colors ${
              bulkActionsMode 
                ? 'bg-purple-100 dark:bg-purple-900/30 border-purple-300 dark:border-purple-700 text-purple-800 dark:text-purple-200' 
                : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-600'
            }`}>
            <CheckSquare className="w-4 h-4" /> {bulkActionsMode ? 'Cancel Selection' : 'Bulk Actions'}
          </button>
        )}
      </div>

      {/* Bulk Actions Toolbar */}
      {bulkActionsMode && (
        <div className="flex flex-wrap items-center gap-3 p-3 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-lg">
          <div className="flex items-center gap-2">
            <CheckSquare className="w-5 h-5 text-purple-600" />
            <span className="text-sm font-medium text-purple-800 dark:text-purple-200">
              {selectedControlIds.size} selected
            </span>
          </div>
          
          <div className="h-6 w-px bg-purple-300 dark:bg-purple-700" />
          
          {/* Select All / Clear */}
          <button
            onClick={selectAllControls}
            className="px-2 py-1 text-xs bg-purple-100 dark:bg-purple-800/50 text-purple-700 dark:text-purple-300 rounded hover:bg-purple-200 dark:hover:bg-purple-800"
          >
            Select All ({filteredControls.length})
          </button>
          {selectedControlIds.size > 0 && (
            <button
              onClick={clearSelection}
              className="px-2 py-1 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Clear
            </button>
          )}

          {selectedControlIds.size > 0 && (
            <>
              <div className="h-6 w-px bg-purple-300 dark:bg-purple-700" />
              
              {/* Move to Section */}
              <select
                onChange={e => { if (e.target.value) bulkMoveControls(e.target.value); }}
                className="px-2 py-1.5 border border-purple-300 dark:border-purple-700 rounded-lg bg-white dark:bg-gray-800 text-xs text-gray-900 dark:text-white"
                defaultValue=""
                disabled={bulkActionLoading}
              >
                <option value="" disabled>Move to...</option>
                {layout.sections.filter(s => s.id !== activeSection).map(s => (
                  <option key={s.id} value={s.id}>{s.label}</option>
                ))}
              </select>

              {/* Set Applicable */}
              <button
                onClick={() => bulkSetApplicable(1)}
                disabled={bulkActionLoading}
                className="flex items-center gap-1 px-2 py-1.5 text-xs bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 border border-green-300 dark:border-green-700 rounded-lg hover:bg-green-200 dark:hover:bg-green-800 disabled:opacity-50"
              >
                <Check className="w-3 h-3" /> Applicable
              </button>

              {/* Set Not Applicable */}
              <button
                onClick={() => bulkSetApplicable(0)}
                disabled={bulkActionLoading}
                className="flex items-center gap-1 px-2 py-1.5 text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                <X className="w-3 h-3" /> Not Applicable
              </button>

              <div className="h-6 w-px bg-purple-300 dark:bg-purple-700" />

              {/* AI Actions Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowBulkActionsMenu(!showBulkActionsMenu)}
                  disabled={bulkActionLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-gradient-to-r from-purple-500 to-indigo-500 text-white rounded-lg hover:from-purple-600 hover:to-indigo-600 disabled:opacity-50"
                >
                  <Sparkles className="w-3.5 h-3.5" /> AI Actions
                  <ChevronDown className="w-3 h-3" />
                </button>
                
                {showBulkActionsMenu && (
                  <div className="absolute top-full left-0 mt-1 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-50">
                    {/* Script Type Selector */}
                    <div className="p-2 border-b border-gray-100 dark:border-gray-700">
                      <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Script Type:</label>
                      <select 
                        value={scriptType}
                        onChange={e => setScriptType(e.target.value as any)}
                        className="w-full px-2 py-1 text-xs border rounded bg-white dark:bg-gray-700 dark:border-gray-600"
                      >
                        <option value="powershell">PowerShell (Windows)</option>
                        <option value="bash">Bash (Linux/Mac)</option>
                        <option value="python">Python (Cross-platform)</option>
                      </select>
                    </div>
                    
                    <button
                      onClick={() => { bulkGenerateScripts(); setShowBulkActionsMenu(false); }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2"
                    >
                      <Terminal className="w-4 h-4 text-blue-500" />
                      <div>
                        <div className="font-medium">Generate Check Scripts</div>
                        <div className="text-xs text-gray-500">Create automated verification</div>
                      </div>
                    </button>
                    
                    <button
                      onClick={() => { bulkReprocessControls(); setShowBulkActionsMenu(false); }}
                      className="w-full px-3 py-2 text-left text-sm hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2 border-t border-gray-100 dark:border-gray-700"
                    >
                      <RefreshCw className="w-4 h-4 text-orange-500" />
                      <div>
                        <div className="font-medium">Re-process with AI</div>
                        <div className="text-xs text-gray-500">Fix Title/Description</div>
                      </div>
                    </button>
                  </div>
                )}
              </div>

              {/* Progress indicator */}
              {bulkActionLoading && (
                <div className="flex items-center gap-2 text-xs text-purple-600 dark:text-purple-400">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {bulkActionProgress.current}/{bulkActionProgress.total}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Controls List */}
      {paginatedControls.length === 0 ? (
        <div className="text-center py-8 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <FileText className="w-10 h-10 mx-auto mb-2 text-gray-300" />
          <p className="text-gray-500 text-sm">No items in this section{searchQuery ? ' matching your search' : ''}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {paginatedControls.map(control => {
            const isEditing = editingId === control.id;
            const isPenaltySection = currentSection?.type === 'penalties';
            const linkedReqs = isPenaltySection ? (penaltyLinks.get(control.id) || []) : [];
            const linkedControls = linkedReqs.map(id => controls.find(c => c.id === id)).filter(Boolean) as GRCControl[];
            const isAiActive = aiActiveId === control.id;
            const isGuidanceOpen = guidanceId === control.id;
            const hasGuidance = isStandard && !!(control.guidance || control.implementation_notes);

            return (
              <div
                key={control.id}
                className={`bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden transition-all ${
                  !currentSection?.readOnly && canEdit && !isEditing ? 'hover:shadow-md cursor-pointer' : ''
                }`}
                onClick={() => {
                  if (!currentSection?.readOnly && canEdit && !isEditing) startEdit(control);
                }}
              >
                <div className="p-4">
                  {/* Header */}
                  <div className="flex items-start justify-between">
                    <div className="flex-1 flex items-start gap-2">
                      {/* Bulk actions checkbox */}
                      {bulkActionsMode && (
                        <input
                          type="checkbox"
                          checked={selectedControlIds.has(control.id)}
                          onChange={(e) => { e.stopPropagation(); toggleControlSelection(control.id); }}
                          onClick={e => e.stopPropagation()}
                          className="mt-1 rounded border-purple-400 text-purple-600 focus:ring-purple-500"
                        />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-xs font-mono bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-0.5 rounded">
                            {control.control_code || control.article_code || `#${control.id}`}
                          </span>
                          {control.chapter_title && (
                            <span className="text-xs text-gray-400 uppercase">• {control.chapter_title}</span>
                          )}
                          {/* Section override indicator */}
                          {control.section_override && (
                            <span className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 px-1.5 py-0.5 rounded flex items-center gap-1"
                              title="Manually moved to this section">
                              <ArrowRightLeft className="w-3 h-3" /> Moved
                            </span>
                          )}
                          {/* ISO 27002 guidance toggle */}
                          {hasGuidance && (
                            <button onClick={e => { e.stopPropagation(); setGuidanceId(isGuidanceOpen ? null : control.id); }}
                              className="text-indigo-500 hover:text-indigo-700 p-0.5 rounded hover:bg-indigo-50 dark:hover:bg-indigo-900/30"
                              title="View ISO 27002 Guidance">
                              <Info className="w-4 h-4" />
                            </button>
                          )}
                          {/* Info button — show full article details and AI explanation */}
                          <button onClick={e => { e.stopPropagation(); setInfoExpandedId(infoExpandedId === control.id ? null : control.id); }}
                            className={`p-0.5 rounded transition-colors ${
                              infoExpandedId === control.id
                                ? 'text-blue-600 bg-blue-100 dark:bg-blue-900/30'
                                : 'text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20'
                            }`}
                            title="View full article info &amp; AI explanation">
                            <Info className="w-4 h-4" />
                          </button>
                        </div>
                        <h4 className="font-semibold text-gray-900 dark:text-white">{control.title}</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 whitespace-pre-line">{control.description}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                      {/* Move to section button — always shown for admin/editor, even in readOnly sections */}
                      {canEdit && !isEditing && (
                        <div className="relative">
                          <button
                            onClick={(e) => { e.stopPropagation(); setMoveMenuId(moveMenuId === control.id ? null : control.id); }}
                            className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                            title="Move to section / Edit"
                          >
                            <MoreVertical className="w-4 h-4" />
                          </button>
                          {moveMenuId === control.id && (
                            <div className="absolute right-0 top-8 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-50 py-1"
                              onClick={e => e.stopPropagation()}>
                              <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide border-b border-gray-100 dark:border-gray-700">
                                Move to Section
                              </div>
                              {layout.sections.filter(s => s.id !== activeSection).map(s => (
                                <button key={s.id}
                                  onClick={() => moveControlToSection(control.id, s.id)}
                                  className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2">
                                  <ArrowRightLeft className="w-3.5 h-3.5 text-gray-400" />
                                  {s.label}
                                </button>
                              ))}
                              {control.section_override && (
                                <>
                                  <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
                                  <button
                                    onClick={() => resetControlSection(control.id)}
                                    className="w-full text-left px-3 py-2 text-sm text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-900/20 flex items-center gap-2">
                                    <X className="w-3.5 h-3.5" />
                                    Reset to auto-assigned
                                  </button>
                                </>
                              )}
                              <div className="border-t border-gray-100 dark:border-gray-700 my-1" />
                              <button
                                onClick={() => { setMoveMenuId(null); startEdit(control); }}
                                className="w-full text-left px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-2">
                                <Edit className="w-3.5 h-3.5 text-gray-400" />
                                Edit Control
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                      {/* Status badge (only for compliance sections) */}
                      {!currentSection?.readOnly && (
                        <span className={`px-2 py-1 rounded text-xs font-medium border ${getStatusBadge(control.status)}`}>
                          {control.status.replace('_', ' ')}
                        </span>
                      )}
                      {/* Applicability (where relevant) */}
                      {currentSection?.showApplicability && (
                        control.applicable === 1 ? (
                          <span className="flex items-center gap-1 text-green-600 dark:text-green-400 text-xs bg-green-50 dark:bg-green-900/30 px-2 py-1 rounded">
                            <Check className="w-3 h-3" /> Applicable
                          </span>
                        ) : control.applicable === 0 ? (
                          <span className="flex items-center gap-1 text-gray-500 text-xs bg-gray-50 dark:bg-gray-700 px-2 py-1 rounded">
                            <X className="w-3 h-3" /> N/A
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-amber-600 text-xs bg-amber-50 dark:bg-amber-900/30 px-2 py-1 rounded">
                            Partial
                          </span>
                        )
                      )}
                    </div>
                  </div>

                  {/* ISO 27002 Guidance Panel */}
                  {isGuidanceOpen && hasGuidance && (
                    <div className="mt-3 p-3 bg-indigo-50 dark:bg-indigo-900/20 text-indigo-800 dark:text-indigo-200 text-sm rounded-lg border border-indigo-100 dark:border-indigo-800" onClick={e => e.stopPropagation()}>
                      <strong className="text-xs uppercase tracking-wide">ISO 27002 Guidance:</strong>
                      <p className="mt-1 whitespace-pre-line">{control.guidance || control.implementation_notes}</p>
                    </div>
                  )}

                  {/* Info Panel — Full article content, chapter context, and AI explanation */}
                  {infoExpandedId === control.id && (
                    <div className="mt-3 p-4 bg-blue-50 dark:bg-blue-900/10 text-gray-800 dark:text-gray-200 text-sm rounded-lg border border-blue-200 dark:border-blue-800 space-y-3" onClick={e => e.stopPropagation()}>
                      <div className="flex items-center justify-between">
                        <strong className="text-xs uppercase tracking-wide text-blue-700 dark:text-blue-300 flex items-center gap-1.5">
                          <Info className="w-3.5 h-3.5" /> Article / Control Details
                        </strong>
                        <button onClick={() => setInfoExpandedId(null)}
                          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-0.5 rounded">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      
                      {/* Control metadata */}
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="bg-white dark:bg-gray-800 rounded p-2 border border-blue-100 dark:border-blue-900">
                          <span className="text-gray-500 dark:text-gray-400 block">Code</span>
                          <span className="font-mono font-medium">{control.control_code || control.article_code || `#${control.id}`}</span>
                        </div>
                        {control.chapter_title && (
                          <div className="bg-white dark:bg-gray-800 rounded p-2 border border-blue-100 dark:border-blue-900">
                            <span className="text-gray-500 dark:text-gray-400 block">Chapter</span>
                            <span className="font-medium">{control.chapter_code ? `${control.chapter_code} — ` : ''}{control.chapter_title}</span>
                          </div>
                        )}
                        {control.section_type && (
                          <div className="bg-white dark:bg-gray-800 rounded p-2 border border-blue-100 dark:border-blue-900">
                            <span className="text-gray-500 dark:text-gray-400 block">Section Type</span>
                            <span className="font-medium capitalize">{control.section_type}</span>
                          </div>
                        )}
                        {control.severity && (
                          <div className="bg-white dark:bg-gray-800 rounded p-2 border border-blue-100 dark:border-blue-900">
                            <span className="text-gray-500 dark:text-gray-400 block">Severity</span>
                            <span className="font-medium capitalize">{control.severity}</span>
                          </div>
                        )}
                      </div>

                      {/* Full title and description */}
                      <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-blue-100 dark:border-blue-900">
                        <h5 className="font-semibold text-sm mb-1">{control.title}</h5>
                        {control.description && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-line">{control.description}</p>
                        )}
                      </div>

                      {/* Guidance / Notes */}
                      {(control.guidance || control.implementation_notes) && (
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-blue-100 dark:border-blue-900">
                          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 block mb-1">SAQ Completion Guidance</span>
                          <p className="text-sm whitespace-pre-line">{control.guidance || control.implementation_notes}</p>
                        </div>
                      )}

                      {/* Expected Testing / Verification Procedures */}
                      {(control.implementation_steps || control.testing_procedures) && (
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-green-100 dark:border-green-900">
                          <span className="text-xs font-medium text-green-600 dark:text-green-400 block mb-1">Expected Testing / Verification</span>
                          <p className="text-sm whitespace-pre-line text-gray-700 dark:text-gray-300">{control.implementation_steps || control.testing_procedures}</p>
                        </div>
                      )}

                      {/* Applicability Notes */}
                      {(control.applicability_reason || control.applicability_notes) && (
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-amber-100 dark:border-amber-900">
                          <span className="text-xs font-medium text-amber-600 dark:text-amber-400 block mb-1">Applicability Notes</span>
                          <p className="text-sm whitespace-pre-line text-gray-700 dark:text-gray-300">{control.applicability_reason || control.applicability_notes}</p>
                        </div>
                      )}

                      {/* Implementation Method */}
                      {control.implementation_method && (
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-purple-100 dark:border-purple-900">
                          <span className="text-xs font-medium text-purple-600 dark:text-purple-400 block mb-1">Implementation Approach</span>
                          <p className="text-sm whitespace-pre-line text-gray-700 dark:text-gray-300">{control.implementation_method}</p>
                        </div>
                      )}

                      {/* Gap Analysis Questions */}
                      {control.gap_questions && (
                        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 border border-rose-100 dark:border-rose-900">
                          <span className="text-xs font-medium text-rose-600 dark:text-rose-400 block mb-1">Gap Analysis Questions</span>
                          <p className="text-sm whitespace-pre-line text-gray-700 dark:text-gray-300">{control.gap_questions}</p>
                        </div>
                      )}

                      {/* AI Explain button */}
                      <div className="pt-2 border-t border-blue-200 dark:border-blue-800">
                        {aiExplainId === control.id && aiExplainResult ? (
                          <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-3 border border-purple-200 dark:border-purple-800">
                            <div className="flex items-center gap-1.5 mb-2 text-xs font-medium text-purple-700 dark:text-purple-300 uppercase tracking-wide">
                              <Sparkles className="w-3.5 h-3.5" /> AI Explanation
                            </div>
                            <div className="text-sm whitespace-pre-line text-gray-700 dark:text-gray-300">{aiExplainResult}</div>
                          </div>
                        ) : (
                          <button
                            onClick={async () => {
                              if (!framework || aiExplainLoading) return;
                              setAiExplainId(control.id);
                              setAiExplainLoading(true);
                              setAiExplainResult('');
                              try {
                                // Build rich context for the AI question
                                const ctrlCode = control.control_code || control.article_code || '';
                                const checkType = control.check_type || 'manual';
                                const isApplicable = Number(control.applicable) === 1;
                                const severity = control.severity || 'medium';
                                const sectionType = control.section_type || '';

                                // Build enhanced question with test script request
                                const canAutomate = checkType === 'automated' || checkType === 'hybrid';
                                
                                const question = controlLanguage === 'Serbian'
                                  ? `Objasni detaljno kontrolu/članak "${ctrlCode} — ${control.title}" iz ${framework.name}.

Obavezno odgovori na:
1. **Šta ova kontrola zahteva** — objasni suštinu zahteva
2. **Obaveze i posledice** — šta se dešava ako nije ispunjeno
3. **Praktični primeri** — konkretni koraci implementacije
4. **Potrebni dokazi** — koji dokumenti/zapisi su potrebni kao dokaz usklađenosti
${isApplicable ? '5. **Sadržaj za ispunjenje** — šta tačno treba da sadrži dokumentacija za ovu kontrolu' : ''}
${canAutomate ? `6. **Test skripta** — napiši test/verifikacionu skriptu (${checkType === 'automated' ? 'automatsku' : 'poluautomatsku'}) koja proverava da li je ova kontrola ispunjena. Koristi PowerShell, Bash ili Python. Objasni šta skripta proverava.` : ''}

Kontrola je tipa: ${checkType}, ozbiljnost: ${severity}, primenljiva: ${isApplicable ? 'DA' : 'NE'}${sectionType ? ', kategorija: ' + sectionType : ''}.`
                                  : `Explain in detail the control/article "${ctrlCode} — ${control.title}" from ${framework.name}.

Please cover:
1. **What this control requires** — explain the core requirement
2. **Obligations and consequences** — what happens if not met
3. **Practical examples** — concrete implementation steps
4. **Required evidence** — what documents/records are needed as proof of compliance
${isApplicable ? '5. **Content for fulfillment** — what exactly should the documentation contain for this control' : ''}
${canAutomate ? `6. **Test/Verification script** — write a ${checkType === 'automated' ? 'fully automated' : 'semi-automated'} test script (PowerShell, Bash, or Python) that verifies whether this control is fulfilled. Explain what the script checks.` : ''}

Control metadata: check_type=${checkType}, severity=${severity}, applicable=${isApplicable ? 'YES' : 'NO'}${sectionType ? ', category=' + sectionType : ''}.`;

                                const result = await grcService.aiRegulationChat(framework.id, question);
                                setAiExplainResult(result.success ? result.answer : (result.error || 'Error'));
                              } catch (err) {
                                setAiExplainResult('Failed to get AI explanation.');
                              } finally {
                                setAiExplainLoading(false);
                              }
                            }}
                            disabled={aiExplainLoading}
                            className="flex items-center gap-2 px-3 py-2 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg text-xs font-medium hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors border border-purple-200 dark:border-purple-800 disabled:opacity-50"
                          >
                            {aiExplainLoading && aiExplainId === control.id ? (
                              <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Analyzing...</>
                            ) : (
                              <><Sparkles className="w-3.5 h-3.5" /> AI: Explain + {(control.check_type === 'automated' || control.check_type === 'hybrid') ? 'Test Script' : 'Guidance'}</>
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Fulfillment & Owner (always visible for compliance sections) */}
                  {!currentSection?.readOnly && (
                    <div className="flex flex-wrap items-center gap-3 mt-3 p-2 bg-gray-50 dark:bg-gray-700/50 rounded-lg text-xs">
                      {control.fulfillment_status && (
                        <span className={`px-2 py-0.5 rounded border font-medium ${getFulfillmentBadge(control.fulfillment_status)}`}>
                          {control.fulfillment_status.replace('_', ' ')}
                        </span>
                      )}
                      {control.owner && (
                        <span className="text-gray-500">Owner: <span className="font-medium text-gray-700 dark:text-gray-300">{control.owner}</span></span>
                      )}
                      {control.evidence_count > 0 && (
                        <span className="text-gray-500">Evidence: <span className="font-medium text-indigo-600">{control.evidence_count}</span></span>
                      )}
                      
                      <div className="ml-auto flex items-center gap-1">
                        {/* Generate Script button (for automated/hybrid checks) */}
                        {(control.check_type === 'automated' || control.check_type === 'hybrid') && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleGenerateScript(control); }}
                            className="flex items-center gap-1 px-2 py-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                            title="Generate Verification Script"
                          >
                            <Terminal className="w-3.5 h-3.5" />
                            <span className="text-xs">Script</span>
                          </button>
                        )}
                        
                        {/* Re-process button */}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleReprocessControl(control); }}
                          className="flex items-center gap-1 px-2 py-1 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded transition-colors"
                          title="Re-process Title/Description with AI"
                        >
                          <RefreshCw className="w-3.5 h-3.5" />
                        </button>
                        
                        {/* Cross-Framework Mapping button */}
                        <button
                          onClick={(e) => { e.stopPropagation(); handleOpenCrossFrameworkMapping(control); }}
                          className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                            (control as any).cross_framework_count > 0 
                              ? 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300' 
                              : 'text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20'
                          }`}
                          title={`Cross-Framework Mappings${(control as any).cross_framework_count > 0 ? ` (${(control as any).cross_framework_count})` : ''}`}
                        >
                          <Globe className="w-3.5 h-3.5" />
                          {(control as any).cross_framework_count > 0 && (
                            <span className="text-xs font-medium">{(control as any).cross_framework_count}</span>
                          )}
                        </button>
                        
                        {/* AI Assist button */}
                        <button
                          onClick={(e) => { e.stopPropagation(); setAiActiveId(isAiActive ? null : control.id); }}
                          className={`flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                            isAiActive ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' : 'text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20'
                          }`}
                          title="AI Guidance"
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                          <span>AI Assist</span>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Penalty Section — Show linked controls */}
                  {isPenaltySection && linkedControls.length > 0 && (
                    <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/10 rounded-lg border border-red-100 dark:border-red-800">
                      <div className="text-xs font-medium text-red-800 dark:text-red-300 mb-2 flex items-center gap-1">
                        <LinkIcon className="w-3 h-3" /> Applies to {linkedControls.length} requirement(s):
                      </div>
                      <div className="space-y-1">
                        {linkedControls.map(lc => (
                          <div key={lc.id} className="text-xs text-red-700 dark:text-red-300 flex items-center gap-2">
                            <span className="font-mono bg-red-100 dark:bg-red-800 px-1.5 py-0.5 rounded">{lc.control_code || lc.article_code}</span>
                            <span>{lc.title}</span>
                            <span className={`ml-auto px-1.5 py-0.5 rounded border text-[10px] ${getStatusBadge(lc.status)}`}>
                              {lc.status.replace('_', ' ')}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Policy links */}
                  {currentSection?.showPolicyLink && control.suggested_policy_names && control.suggested_policy_names.length > 0 && !isEditing && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      <span className="text-xs text-gray-500 flex items-center gap-1"><LinkIcon className="w-3 h-3" /> Policies:</span>
                      {control.suggested_policy_names.map((pName, i) => (
                        <button key={i}
                          onClick={(e) => { e.stopPropagation(); onNavigate?.('policy', { searchQuery: pName }); }}
                          className="text-xs bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 px-1.5 py-0.5 rounded border border-blue-100 dark:border-blue-800 hover:bg-blue-100">
                          {pName}
                        </button>
                      ))}
                    </div>
                  )}

                  {/* AI Advisor */}
                  {isAiActive && (
                    <div className="mt-3" onClick={e => e.stopPropagation()}>
                      <SoAAdvisorCard
                        control={toSoAControl(control)}
                        frameworkName={framework?.name || framework?.code}
                        frameworkContext={{
                          code: framework?.code,
                          type: framework?.framework_type,
                          language: controlLanguage,
                        }}
                        onApply={(justification, owner, steps, docs, summary) => handleApplyAI(control.id, justification, owner, steps, docs, summary)}
                        onClose={() => setAiActiveId(null)}
                      />
                    </div>
                  )}

                  {/* Edit Form (inline) */}
                  {isEditing && !currentSection?.readOnly && (
                    <div className="mt-4 space-y-3 bg-gray-50 dark:bg-gray-700 p-4 rounded-lg border border-gray-200 dark:border-gray-600" onClick={e => e.stopPropagation()}>
                      {/* Title & Description editing */}
                      <div className="space-y-3 pb-3 border-b border-gray-200 dark:border-gray-600">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                          <input type="text" value={editForm.title || ''}
                            onChange={e => setEditForm({ ...editForm, title: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
                            placeholder="Control title" />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                          <textarea value={editForm.description || ''}
                            onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                            rows={3}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
                            placeholder="Control description" />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {currentSection?.showApplicability && (
                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Applicability</label>
                            <select value={editForm.applicable ?? 1}
                              onChange={e => setEditForm({ ...editForm, applicable: Number(e.target.value) })}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white">
                              <option value={1}>Applicable</option>
                              <option value={2}>Partially Applicable</option>
                              <option value={0}>Not Applicable</option>
                            </select>
                          </div>
                        )}
                        {currentSection?.showFulfillment && (
                          <div>
                            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Fulfillment Status</label>
                            <select value={editForm.fulfillment_status || 'not_fulfilled'}
                              onChange={e => setEditForm({ ...editForm, fulfillment_status: e.target.value as any })}
                              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white">
                              <option value="not_fulfilled">Not Fulfilled</option>
                              <option value="partially_fulfilled">Partially Fulfilled</option>
                              <option value="fulfilled">Fulfilled</option>
                            </select>
                          </div>
                        )}
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Compliance Status</label>
                          <select value={editForm.status || 'not_assessed'}
                            onChange={e => setEditForm({ ...editForm, status: e.target.value as any })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white">
                            <option value="not_assessed">Not Assessed</option>
                            <option value="compliant">Compliant</option>
                            <option value="partial">Partial</option>
                            <option value="non_compliant">Non-Compliant</option>
                            <option value="not_applicable">Not Applicable</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Owner</label>
                          <input type="text" value={editForm.owner || ''}
                            onChange={e => setEditForm({ ...editForm, owner: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
                            placeholder="e.g. CISO, DPO" />
                        </div>
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Justification / Rationale</label>
                        <textarea value={editForm.applicability_justification || ''}
                          onChange={e => setEditForm({ ...editForm, applicability_justification: e.target.value })}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
                          placeholder="Why is this applicable/not applicable? How is it fulfilled?" />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Implementation Notes</label>
                        <textarea value={editForm.implementation_notes || ''}
                          onChange={e => setEditForm({ ...editForm, implementation_notes: e.target.value })}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
                          placeholder="Describe how this is implemented..." />
                      </div>

                      <div>
                        <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Implementation Steps</label>
                        <textarea value={(editForm.implementation_steps || []).join('\n')}
                          onChange={e => setEditForm({ ...editForm, implementation_steps: e.target.value.split('\n').filter(Boolean) })}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-white"
                          placeholder="Step 1&#10;Step 2&#10;Step 3" />
                      </div>

                      {/* Link Policies */}
                      {currentSection?.showPolicyLink && (
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 flex items-center gap-1">
                            <LinkIcon className="w-3 h-3" /> {t.controls.linkPolicyAsEvidence}
                          </label>
                          <div className="max-h-32 overflow-y-auto border dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 p-2 space-y-1">
                            {policies.map(p => {
                              // Support both string and number IDs for backward compatibility
                              const policyIdNum = Number(p.id);
                              const policyIdStr = String(p.id);
                              const linkedIds = editForm.linked_policy_ids || [];
                              const isLinked = linkedIds.some(id => String(id) === policyIdStr || id === policyIdNum);
                              return (
                                <label key={p.id} className="flex items-center text-sm hover:bg-gray-50 dark:hover:bg-gray-700 p-1 rounded cursor-pointer text-gray-900 dark:text-gray-100">
                                  <input type="checkbox" checked={isLinked}
                                    className="rounded border-gray-300 text-indigo-600 mr-2"
                                    onChange={() => {
                                      const ids = [...linkedIds];
                                      // Use string ID for consistency with Policy type
                                      const idToUse = isNaN(policyIdNum) ? policyIdStr : policyIdNum;
                                      if (isLinked) {
                                        // Remove the ID (handle both string and number forms)
                                        const newIds = ids.filter(id => String(id) !== policyIdStr && id !== policyIdNum);
                                        setEditForm({ ...editForm, linked_policy_ids: newIds as number[] });
                                      } else {
                                        // Add the ID
                                        setEditForm({ ...editForm, linked_policy_ids: [...ids, idToUse] as number[] });
                                      }
                                    }}
                                  />
                                  <span className="truncate">{p.title}</span>
                                </label>
                              );
                            })}
                            {policies.length === 0 && (
                              <p className="text-xs text-gray-500 italic">{t.controls.noPoliciesAvailable}</p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Evidence Management Button */}
                      <div>
                        <button
                          type="button"
                          onClick={() => setEvidenceModalControl(control)}
                          className="flex items-center gap-2 px-4 py-2 text-sm bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 rounded-lg hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors border border-emerald-200 dark:border-emerald-800"
                        >
                          <Paperclip className="w-4 h-4" />
                          {t.proof.manageEvidence || 'Manage Evidence'}
                          {control.evidence_count > 0 && (
                            <span className="ml-1 px-2 py-0.5 bg-emerald-200 dark:bg-emerald-800 rounded-full text-xs font-medium">
                              {control.evidence_count}
                            </span>
                          )}
                        </button>
                      </div>

                      <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                        <button onClick={() => setEditingId(null)} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors">{t.actions.cancel}</button>
                        <button onClick={saveEdit} className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700">{t.actions.save}</button>
                      </div>
                    </div>
                  )}

                  {/* Read-only mode details */}
                  {!isEditing && !currentSection?.readOnly && (
                    <div className="mt-2 space-y-1">
                      {control.applicability_justification && (
                        <p className="text-xs text-gray-500 dark:text-gray-400"><span className="font-medium">Justification:</span> {control.applicability_justification}</p>
                      )}
                      {control.fulfillment_rationale && (
                        <p className="text-xs text-gray-500 dark:text-gray-400"><span className="font-medium">Method:</span> {control.fulfillment_rationale}</p>
                      )}
                      {control.implementation_steps && control.implementation_steps.length > 0 && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700 p-2 rounded border border-gray-100 dark:border-gray-600">
                          <span className="font-medium block mb-1">Implementation Steps:</span>
                          <ul className="list-disc list-inside pl-1">{control.implementation_steps.map((s, i) => <li key={i}>{s}</li>)}</ul>
                        </div>
                      )}
                      {canEdit && !currentSection?.readOnly && (
                        <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                          <span className="text-xs text-gray-400 flex items-center gap-1"><Edit className="w-3 h-3" /> Click to edit</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {sortedControls.length > pageSize && (
        <Pagination currentPage={currentPage} totalPages={totalPages} pageSize={pageSize}
          onPageChange={setCurrentPage} onPageSizeChange={(s) => { setPageSize(s); setCurrentPage(1); }} totalItems={sortedControls.length} />
      )}

      {/* Footer */}
      <div className="text-sm text-gray-500 text-center">
        {currentSection?.label}: {filteredControls.length} of {currentControls.length} items
        {controls.length > 0 && ` • Total: ${controls.length} controls across ${layout.sections.length} sections`}
      </div>

      {/* Evidence Linking Modal */}
      {evidenceModalControl && frameworkId && (
        <EvidenceLinkingModal
          control={evidenceModalControl}
          frameworkId={frameworkId}
          isOpen={!!evidenceModalControl}
          onClose={() => setEvidenceModalControl(null)}
          onLink={(controlId, evidenceIds) => {
            // Update control with linked evidence
            setControls(prev => prev.map(c => 
              c.id === controlId ? { ...c, linked_policy_ids: evidenceIds } : c
            ));
            setEvidenceModalControl(null);
          }}
        />
      )}

      {/* Control Edit Modal */}
      {editModalControl && framework && (
        <ControlEditModal
          control={editModalControl}
          framework={framework}
          isOpen={!!editModalControl}
          onClose={() => setEditModalControl(null)}
          onSave={handleEditModalSave}
          onOpenEvidence={() => {
            setEvidenceModalControl(editModalControl);
          }}
        />
      )}

      {/* Script Generation Modal */}
      {scriptModalControl && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <Terminal className="w-5 h-5 text-blue-500" />
                  Verification Script
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {scriptModalControl.control_code} — {scriptModalControl.title}
                </p>
              </div>
              <button onClick={() => { setScriptModalControl(null); setGeneratedScript(null); }} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[70vh]">
              {scriptLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
                  <span className="ml-3 text-gray-600 dark:text-gray-400">Generating {scriptType} script...</span>
                </div>
              ) : generatedScript ? (
                <div className="space-y-4">
                  <div className="flex gap-2 mb-4">
                    {(['powershell', 'bash', 'python'] as const).map(type => (
                      <button
                        key={type}
                        onClick={() => { setScriptType(type); handleGenerateScript(scriptModalControl); }}
                        className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                          scriptType === type 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                        }`}
                      >
                        {type === 'powershell' ? 'PowerShell' : type === 'bash' ? 'Bash' : 'Python'}
                      </button>
                    ))}
                  </div>
                  
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Script ({scriptType})</label>
                      <button
                        onClick={() => navigator.clipboard.writeText(generatedScript.script)}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                      >
                        <Copy className="w-3 h-3" /> Copy
                      </button>
                    </div>
                    <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto text-sm font-mono whitespace-pre-wrap">
                      {generatedScript.script}
                    </pre>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">Explanation</label>
                    <p className="text-sm text-gray-600 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
                      {generatedScript.explanation}
                    </p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-2">Expected Output (When Compliant)</label>
                    <pre className="bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300 p-3 rounded-lg text-sm">
                      {generatedScript.expectedOutput}
                    </pre>
                  </div>
                </div>
              ) : null}
            </div>
            
            {generatedScript && (
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                <button
                  onClick={() => { setScriptModalControl(null); setGeneratedScript(null); }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveScript}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
                >
                  <Paperclip className="w-4 h-4" /> Save to Control
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Re-process Control Modal */}
      {reprocessModalControl && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                  <RefreshCw className="w-5 h-5 text-orange-500" />
                  Re-process Control Content
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {reprocessModalControl.control_code || reprocessModalControl.article_code}
                </p>
              </div>
              <button onClick={() => { setReprocessModalControl(null); setReprocessResult(null); }} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[70vh]">
              {reprocessLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
                  <span className="ml-3 text-gray-600 dark:text-gray-400">Analyzing content...</span>
                </div>
              ) : reprocessResult ? (
                <div className="space-y-4">
                  {reprocessResult.needsUpdate ? (
                    <>
                      <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-3">
                        <div className="text-sm font-medium text-orange-800 dark:text-orange-300 mb-2">Changes Detected:</div>
                        <ul className="list-disc list-inside text-sm text-orange-700 dark:text-orange-400 space-y-1">
                          {reprocessResult.changes.map((change, i) => (
                            <li key={i}>{change}</li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1">Original Title</label>
                          <div className="text-sm bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-800 dark:text-red-300">
                            {reprocessModalControl.title}
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1">Corrected Title</label>
                          <div className="text-sm bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-green-800 dark:text-green-300">
                            {reprocessResult.title}
                          </div>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1">Original Description</label>
                          <div className="text-sm bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-red-800 dark:text-red-300 max-h-40 overflow-y-auto">
                            {reprocessModalControl.description}
                          </div>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide block mb-1">Corrected Description</label>
                          <div className="text-sm bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-green-800 dark:text-green-300 max-h-40 overflow-y-auto">
                            {reprocessResult.description}
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-6 text-center">
                      <Check className="w-12 h-12 text-green-500 mx-auto mb-3" />
                      <div className="text-lg font-medium text-green-800 dark:text-green-300">Content is Correct</div>
                      <p className="text-sm text-green-600 dark:text-green-400 mt-1">No changes needed for this control.</p>
                    </div>
                  )}
                </div>
              ) : null}
            </div>
            
            {reprocessResult && reprocessResult.needsUpdate && (
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                <button
                  onClick={() => { setReprocessModalControl(null); setReprocessResult(null); }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  Keep Original
                </button>
                <button
                  onClick={handleApplyReprocess}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 flex items-center gap-2"
                >
                  <Check className="w-4 h-4" /> Apply Changes
                </button>
              </div>
            )}
            
            {reprocessResult && !reprocessResult.needsUpdate && (
              <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex justify-end">
                <button
                  onClick={() => { setReprocessModalControl(null); setReprocessResult(null); }}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
                >
                  Close
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Verification Modal */}
      {showVerificationModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/50 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Extraction Verification</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Check if controls were correctly extracted from the document</p>
                </div>
              </div>
              <button onClick={() => setShowVerificationModal(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4">
              {verificationLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
                </div>
              ) : verificationResult?.success ? (
                <div className="space-y-6">
                  {/* Stats Overview */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg text-center">
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">{verificationResult.stats?.total_controls || 0}</div>
                      <div className="text-sm text-gray-500">Total Controls</div>
                    </div>
                    <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg text-center">
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">{verificationResult.stats?.chapters_count || 0}</div>
                      <div className="text-sm text-gray-500">Chapters</div>
                    </div>
                    <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg text-center">
                      <div className="text-2xl font-bold text-gray-900 dark:text-white">{verificationResult.stats?.sections_count || 0}</div>
                      <div className="text-sm text-gray-500">Sections</div>
                    </div>
                    <div className="p-4 bg-gray-50 dark:bg-gray-900/50 rounded-lg text-center">
                      <div className={`text-2xl font-bold ${verificationResult.stats?.controls_without_chapters > 0 ? 'text-amber-600' : 'text-green-600'}`}>
                        {verificationResult.stats?.controls_with_chapters || 0}
                      </div>
                      <div className="text-sm text-gray-500">With Chapters</div>
                    </div>
                  </div>
                  
                  {/* Issues */}
                  {verificationResult.issues && verificationResult.issues.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900 dark:text-white">Issues Found</h4>
                      {verificationResult.issues.map((issue: any, idx: number) => (
                        <div 
                          key={idx}
                          className={`p-3 rounded-lg border ${
                            issue.severity === 'high' ? 'bg-red-50 border-red-200 dark:bg-red-900/20 dark:border-red-800' :
                            issue.severity === 'medium' ? 'bg-yellow-50 border-yellow-200 dark:bg-yellow-900/20 dark:border-yellow-800' :
                            'bg-blue-50 border-blue-200 dark:bg-blue-900/20 dark:border-blue-800'
                          }`}
                        >
                          <div className="flex items-center gap-2">
                            <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                              issue.severity === 'high' ? 'bg-red-100 text-red-700 dark:bg-red-800 dark:text-red-200' :
                              issue.severity === 'medium' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-800 dark:text-yellow-200' :
                              'bg-blue-100 text-blue-700 dark:bg-blue-800 dark:text-blue-200'
                            }`}>
                              {issue.severity.toUpperCase()}
                            </span>
                            <span className="text-sm text-gray-700 dark:text-gray-300">{issue.message}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* No issues */}
                  {(!verificationResult.issues || verificationResult.issues.length === 0) && (
                    <div className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                      <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                        <Check className="w-5 h-5" />
                        <span className="font-medium">All checks passed! Extraction looks good.</span>
                      </div>
                    </div>
                  )}
                  
                  {/* Sections */}
                  {verificationResult.sections && verificationResult.sections.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900 dark:text-white">Detected Sections ({verificationResult.sections.length})</h4>
                      <div className="grid gap-2 max-h-40 overflow-y-auto">
                        {verificationResult.sections.map((sec: any) => (
                          <div key={sec.id} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-900/50 rounded">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">{sec.label}</span>
                            <span className="text-xs text-gray-500">({sec.chapter_codes?.join(', ')})</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Control Code Patterns */}
                  {verificationResult.stats?.control_code_patterns && (
                    <div className="space-y-2">
                      <h4 className="font-medium text-gray-900 dark:text-white">Control Code Patterns</h4>
                      <div className="flex flex-wrap gap-2">
                        {Object.entries(verificationResult.stats.control_code_patterns).map(([pattern, count]: [string, any]) => (
                          <span key={pattern} className="px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-sm">
                            {pattern}: <strong>{count}</strong>
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                  <p className="text-red-700 dark:text-red-300">{verificationResult?.error || 'Verification failed'}</p>
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <button
                onClick={handleReprocessSections}
                disabled={reprocessLoading || verificationLoading}
                className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
              >
                {reprocessLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                Re-process Sections
              </button>
              <button
                onClick={() => setShowVerificationModal(false)}
                className="px-4 py-2 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cross-Framework Mapping Modal */}
      {crossFrameworkModalControl && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-teal-100 dark:bg-teal-900/50 flex items-center justify-center">
                  <Globe className="w-5 h-5 text-teal-600 dark:text-teal-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Cross-Framework Mappings</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {crossFrameworkModalControl.control_code || crossFrameworkModalControl.article_code}: {crossFrameworkModalControl.title.substring(0, 50)}...
                  </p>
                </div>
              </div>
              <button 
                onClick={() => { setCrossFrameworkModalControl(null); setCrossFrameworkMappings([]); }}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* AI Mapping Section */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              <div className="flex items-center gap-3">
                <select
                  value={selectedTargetFramework || ''}
                  onChange={(e) => setSelectedTargetFramework(Number(e.target.value) || null)}
                  className="flex-1 px-3 py-2 border border-gray-200 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm"
                  disabled={crossFrameworkLoading}
                >
                  <option value="">Select target framework...</option>
                  {availableFrameworks.map(f => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
                <button
                  onClick={handleAiCrossFrameworkMapping}
                  disabled={!selectedTargetFramework || crossFrameworkLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {crossFrameworkLoading ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                  <span>AI Map Controls</span>
                </button>
              </div>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Select a framework and click "AI Map Controls" to find related requirements using AI analysis.
              </p>
            </div>
            
            {/* Mappings List */}
            <div className="flex-1 overflow-y-auto p-4">
              {crossFrameworkLoading && crossFrameworkMappings.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
                </div>
              ) : crossFrameworkMappings.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Globe className="w-12 h-12 mx-auto mb-3 opacity-30" />
                  <p>No cross-framework mappings yet.</p>
                  <p className="text-sm">Select a framework and run AI analysis to find related controls.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {crossFrameworkMappings.map((mapping, idx) => (
                    <div 
                      key={`${mapping.target_control_id}-${idx}`}
                      className="p-4 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-teal-300 dark:hover:border-teal-600 transition-colors"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="px-2 py-0.5 text-xs font-medium rounded bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                              {mapping.target_framework_name}
                            </span>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                              mapping.mapping_type === 'equivalent' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' :
                              mapping.mapping_type === 'overlapping' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' :
                              mapping.mapping_type === 'partial' ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' :
                              'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
                            }`}>
                              {mapping.mapping_type}
                            </span>
                            <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                              mapping.relevance_score >= 80 ? 'bg-green-100 text-green-700' :
                              mapping.relevance_score >= 60 ? 'bg-blue-100 text-blue-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {mapping.relevance_score}% match
                            </span>
                            {mapping.created_by_ai && (
                              <span className="flex items-center gap-1 px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300">
                                <Sparkles className="w-3 h-3" /> AI
                              </span>
                            )}
                          </div>
                          <div className="font-medium text-gray-900 dark:text-white">
                            <span className="text-teal-600 dark:text-teal-400">{mapping.target_control_code}</span>
                            {mapping.target_control_code && mapping.target_control_title && ': '}
                            {mapping.target_control_title}
                          </div>
                          {mapping.explanation && (
                            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{mapping.explanation}</p>
                          )}
                        </div>
                        <button
                          onClick={() => handleDeleteCrossFrameworkMapping(mapping.target_control_id)}
                          className="p-1.5 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                          title="Remove mapping"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="text-sm text-gray-500">
                {crossFrameworkMappings.length} mapping{crossFrameworkMappings.length !== 1 ? 's' : ''} configured
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setCrossFrameworkModalControl(null); setCrossFrameworkMappings([]); }}
                  className="px-4 py-2 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveCrossFrameworkMappings}
                  disabled={crossFrameworkLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50"
                >
                  {crossFrameworkLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Save Mappings
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
